import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { XMarkIcon, PencilIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { chartOfAccountsService } from '../../../services/accounting/chart-of-accounts';
import toast from 'react-hot-toast';

/**
 * EditAccountModal Component
 * 
 * Modal for editing existing chart of accounts entries with validation
 * and change tracking
 */
const EditAccountModal = ({ isOpen, account, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  const [formData, setFormData] = useState({
    accountCode: '',
    accountName: '',
    accountNameAr: '',
    accountType: 'asset',
    category: '',
    description: '',
    openingBalance: 0,
    normalBalance: 'debit',
    isActive: true,
    allowManualEntries: true,
    requireDepartment: false,
    isTaxRelated: false,
    isVatAccount: false,
    isZakatAccount: false
  });

  // Account type configurations
  const accountTypes = [
    { value: 'asset', label: t('coa.account_types.asset', 'Asset'), normalBalance: 'debit' },
    { value: 'liability', label: t('coa.account_types.liability', 'Liability'), normalBalance: 'credit' },
    { value: 'equity', label: t('coa.account_types.equity', 'Equity'), normalBalance: 'credit' },
    { value: 'revenue', label: t('coa.account_types.revenue', 'Revenue'), normalBalance: 'credit' },
    { value: 'expense', label: t('coa.account_types.expense', 'Expense'), normalBalance: 'debit' }
  ];

  const categories = {
    asset: [
      { value: 'current_asset', label: t('coa.categories.current_asset', 'Current Asset') },
      { value: 'fixed_asset', label: t('coa.categories.fixed_asset', 'Fixed Asset') },
      { value: 'intangible_asset', label: t('coa.categories.intangible_asset', 'Intangible Asset') }
    ],
    liability: [
      { value: 'current_liability', label: t('coa.categories.current_liability', 'Current Liability') },
      { value: 'long_term_liability', label: t('coa.categories.long_term_liability', 'Long-term Liability') }
    ],
    equity: [
      { value: 'equity', label: t('coa.categories.equity', 'Equity') }
    ],
    revenue: [
      { value: 'operating_revenue', label: t('coa.categories.operating_revenue', 'Operating Revenue') },
      { value: 'other_revenue', label: t('coa.categories.other_revenue', 'Other Revenue') }
    ],
    expense: [
      { value: 'operating_expense', label: t('coa.categories.operating_expense', 'Operating Expense') },
      { value: 'administrative_expense', label: t('coa.categories.administrative_expense', 'Administrative Expense') },
      { value: 'other_expense', label: t('coa.categories.other_expense', 'Other Expense') }
    ]
  };

  // Initialize form data when account changes
  useEffect(() => {
    if (isOpen && account) {
      setFormData({
        accountCode: account.accountCode || '',
        accountName: account.accountName || '',
        accountNameAr: account.accountNameAr || '',
        accountType: account.accountType || 'asset',
        category: account.category || '',
        description: account.description || '',
        openingBalance: account.openingBalance || 0,
        normalBalance: account.normalBalance || 'debit',
        isActive: account.isActive !== false,
        allowManualEntries: account.allowManualEntries !== false,
        requireDepartment: account.requireDepartment || false,
        isTaxRelated: account.isTaxRelated || false,
        isVatAccount: account.isVatAccount || false,
        isZakatAccount: account.isZakatAccount || false
      });
      setErrors({});
      setHasChanges(false);
    }
  }, [isOpen, account]);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Check if there are changes
      if (account) {
        const hasChanged = Object.keys(newData).some(key => {
          const originalValue = account[key];
          const newValue = newData[key];
          
          // Handle boolean fields
          if (typeof originalValue === 'boolean' || typeof newValue === 'boolean') {
            return Boolean(originalValue) !== Boolean(newValue);
          }
          
          // Handle numeric fields
          if (key === 'openingBalance') {
            return Number(originalValue || 0) !== Number(newValue || 0);
          }
          
          // Handle string fields
          return String(originalValue || '') !== String(newValue || '');
        });
        
        setHasChanges(hasChanged);
      }
      
      return newData;
    });
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Account Code validation
    if (!formData.accountCode) {
      newErrors.accountCode = t('coa.validation.code_required', 'Account code is required');
    } else if (!/^\d{4}$/.test(formData.accountCode)) {
      newErrors.accountCode = t('coa.validation.code_format', 'Account code must be exactly 4 digits');
    }
    
    // Account Name validation
    if (!formData.accountName.trim()) {
      newErrors.accountName = t('coa.validation.name_required', 'Account name is required');
    }
    
    // Category validation
    if (!formData.category) {
      newErrors.category = t('coa.validation.category_required', 'Category is required');
    }
    
    // Opening balance validation
    if (isNaN(formData.openingBalance)) {
      newErrors.openingBalance = t('coa.validation.balance_numeric', 'Opening balance must be a number');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    if (!hasChanges) {
              toast(t('coa.info.no_changes', 'No changes to save'));
      return;
    }
    
    setLoading(true);
    
    try {
      await chartOfAccountsService.updateAccount(account.id, {
        ...formData,
        updatedBy: currentUser?.uid || 'system',
        openingBalance: parseFloat(formData.openingBalance) || 0
      });
      
      onSuccess();
    } catch (error) {
      console.error('Error updating account:', error);
      
      if (error.message.includes('already exists')) {
        setErrors({ accountCode: t('coa.validation.code_exists', 'Account code already exists') });
      } else if (error.message.includes('4 digits')) {
        setErrors({ accountCode: t('coa.validation.code_format', 'Account code must be exactly 4 digits') });
      } else {
        toast.error(t('coa.error.update_failed', 'Failed to update account'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !account) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <PencilIcon className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('coa.edit_account', 'Edit Account')}
              </h2>
              <p className="text-sm text-gray-500">
                {account.accountCode} - {account.accountName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('coa.account_code', 'Account Code')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.accountCode}
                  onChange={(e) => handleInputChange('accountCode', e.target.value)}
                  className={`input input-bordered w-full ${errors.accountCode ? 'border-red-500' : ''}`}
                  placeholder="1000"
                  maxLength={4}
                />
                {errors.accountCode && (
                  <p className="text-red-500 text-sm mt-1">{errors.accountCode}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('coa.account_type', 'Account Type')} <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.accountType}
                  onChange={(e) => handleInputChange('accountType', e.target.value)}
                  className="select select-bordered w-full"
                >
                  {accountTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('coa.account_name', 'Account Name (English)')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.accountName}
                onChange={(e) => handleInputChange('accountName', e.target.value)}
                className={`input input-bordered w-full ${errors.accountName ? 'border-red-500' : ''}`}
                placeholder={t('coa.account_name_placeholder', 'Enter account name in English')}
              />
              {errors.accountName && (
                <p className="text-red-500 text-sm mt-1">{errors.accountName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('coa.account_name_ar', 'Account Name (Arabic)')}
              </label>
              <input
                type="text"
                value={formData.accountNameAr}
                onChange={(e) => handleInputChange('accountNameAr', e.target.value)}
                className="input input-bordered w-full"
                placeholder={t('coa.account_name_ar_placeholder', 'Enter account name in Arabic')}
                dir="rtl"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('coa.category', 'Category')} <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  className={`select select-bordered w-full ${errors.category ? 'border-red-500' : ''}`}
                >
                  <option value="">{t('coa.select_category', 'Select Category')}</option>
                  {categories[formData.accountType]?.map(category => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
                {errors.category && (
                  <p className="text-red-500 text-sm mt-1">{errors.category}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('coa.normal_balance', 'Normal Balance')}
                </label>
                <select
                  value={formData.normalBalance}
                  onChange={(e) => handleInputChange('normalBalance', e.target.value)}
                  className="select select-bordered w-full"
                >
                  <option value="debit">{t('coa.debit', 'Debit')}</option>
                  <option value="credit">{t('coa.credit', 'Credit')}</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('coa.opening_balance', 'Opening Balance')}
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.openingBalance}
                onChange={(e) => handleInputChange('openingBalance', e.target.value)}
                className={`input input-bordered w-full ${errors.openingBalance ? 'border-red-500' : ''}`}
                placeholder="0.00"
              />
              {errors.openingBalance && (
                <p className="text-red-500 text-sm mt-1">{errors.openingBalance}</p>
              )}
              {account.currentBalance !== undefined && (
                <p className="text-sm text-gray-600 mt-1">
                  {t('coa.current_balance', 'Current Balance')}: {account.currentBalance?.toLocaleString()} SAR
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('coa.description', t('common.description'))}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows="3"
                className="textarea textarea-bordered w-full"
                placeholder={t('coa.description_placeholder', 'Optional description for this account')}
              />
            </div>

            {/* Account Settings */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">
                {t('coa.account_settings', 'Account Settings')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    className="form-checkbox h-4 w-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">
                    {t('coa.is_active', 'Active Account')}
                  </span>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.allowManualEntries}
                    onChange={(e) => handleInputChange('allowManualEntries', e.target.checked)}
                    className="form-checkbox h-4 w-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">
                    {t('coa.allow_manual_entries', 'Allow Manual Entries')}
                  </span>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.requireDepartment}
                    onChange={(e) => handleInputChange('requireDepartment', e.target.checked)}
                    className="form-checkbox h-4 w-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">
                    {t('coa.require_department', 'Require Department')}
                  </span>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.isTaxRelated}
                    onChange={(e) => handleInputChange('isTaxRelated', e.target.checked)}
                    className="form-checkbox h-4 w-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">
                    {t('coa.is_tax_related', 'Tax Related Account')}
                  </span>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.isVatAccount}
                    onChange={(e) => handleInputChange('isVatAccount', e.target.checked)}
                    className="form-checkbox h-4 w-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">
                    {t('coa.is_vat_account', 'VAT Account')}
                  </span>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.isZakatAccount}
                    onChange={(e) => handleInputChange('isZakatAccount', e.target.checked)}
                    className="form-checkbox h-4 w-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">
                    {t('coa.is_zakat_account', 'Zakat Account')}
                  </span>
                </label>
              </div>
            </div>

            {/* Change Indicator */}
            {hasChanges && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  {t('coa.unsaved_changes', 'You have unsaved changes')}
                </p>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 flex-shrink-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            disabled={loading}
          >
            {t('common.cancel', t('common.cancel'))}
          </button>
          <button
            onClick={handleSubmit}
            className={`btn ${hasChanges ? 'btn-primary' : 'btn-secondary'}`}
            disabled={loading || !hasChanges}
          >
            {loading ? (
              <>
                <LoadingSpinner size="sm" />
                {t('common.saving', 'Saving...')}
              </>
            ) : (
              <>
                <PencilIcon className="w-4 h-4 mr-2" />
                {t('common.save_changes', 'Save Changes')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditAccountModal;